// Where every ingot on the board was a moment ago, and which pass moved it.
//
// A turn's industry is four things in order — lanes advance, machines take what
// they want off them, ovens cook, everything hands on — and it used to land on
// screen as one instantaneous change. There was a slide animation for it, and
// it had never once run: its bookkeeping was an `Int8Array(128)` indexed by
// *packed squares*, which start at 524,800, so every read was `undefined` and
// every write a silent no-op.
//
// **The engine says what moved; this says where it was.** The tick reports a
// `CargoLeg` per unit that changed places, because it is the only thing that
// can — a unit is a packed word with no id, two bars of iron are the same word,
// and the board after a tick cannot distinguish a merge from an arrival. What
// is left is remembering the *last drawn* position of each ingot so there is
// something to animate from.
//
// Pure, and no Three.js: a spot is a square plus an offset within it, and the
// world-space lerp belongs where the ground height is known. That is also what
// keeps the offsets right when new ground is still rising.

import type { CargoLeg } from "src/api";

/** One of the four industry passes, as the contract names them. */
export type Phase = CargoLeg["phase"];

/** The order a turn happens in, and so the order it is played back. */
const ORDER: readonly Phase[] = ["flow", "grab", "cook", "handoff"];

/**
 * Where one ingot sits: which square, and where in that square's pile.
 *
 * Not a world position, because the square's own height changes — ground that
 * is rising, a belt deck squashed under a piece — and a remembered world point
 * would be a frame out of date the moment either moved.
 */
export interface IngotSpot {
  square: number;
  /** Offsets from the square's centre, in world units. */
  x: number;
  y: number;
  z: number;
}

/**
 * An ingot with no pile to draw it: between a lane and a machine's hands.
 *
 * A machine's buffers and oven are one box on screen, not a pile of slots, so
 * a unit going into or coming out of one is not in anybody's layout while it
 * travels. Without these the payoff moment of a whole lane — the ore actually
 * going in — is the one frame nothing is drawn for.
 */
export interface Transfer {
  from: IngotSpot;
  to: IngotSpot;
  phase: Phase;
  /** True when it is being consumed, so it shrinks rather than lands. */
  into: boolean;
}

/** Where an ingot drawn by a pile came from, and when it makes the trip. */
export interface Arrival {
  from: IngotSpot;
  phase: Phase;
}

/**
 * A unit's identity, which is only ever its position in a pile.
 *
 * Packing rather than a string because this is built and read for every ingot
 * on the board every frame. Thirty-two slots is far past `MAX_PILE`, and a
 * packed square is under 2^20, so the product stays an exact integer.
 */
export const spotKey = (square: number, index: number): number => square * 32 + index;

/** How long one pass takes. Four of them back to back is the whole run. */
export const CARGO_PHASE_SECONDS = 0.3;

/** Where a square's machine draws what it is holding. Supplied by the view. */
export type HandsAt = (square: number) => IngotSpot;

export class CargoMotion {
  /** Where each ingot came to rest last time the board was drawn. */
  private last = new Map<number, IngotSpot>();
  private origin = new Map<number, Arrival>();
  private transfers: Transfer[] = [];
  private pulses = new Set<number>();
  /** The non-empty passes, in order. An empty pass is never waited for. */
  private queue: Phase[] = [];
  private at = 0;
  /** 0..1 through the pass currently running. */
  private phase = 1;

  /**
   * A tick happened: work out where each ingot has come from, and when.
   *
   * Only *changes* are reported, so anything the list does not mention is
   * assumed to have stayed at its own key — which is not the same as not
   * moving, because a pile that lost two ingots is drawn in a different shape.
   * `spotFor` resolves both cases the same way.
   */
  begin(moves: readonly CargoLeg[], hands: HandsAt): void {
    this.origin.clear();
    this.transfers = [];
    this.pulses.clear();

    for (const move of moves) {
      // A cook is the one pass that moves nothing: the oven and the banks are
      // the same box, so there is nowhere for the ingot to go.
      if (move.phase === "cook") {
        this.pulses.add(move.to);
        continue;
      }

      // A slot of -1 is the square's own hands rather than a slot in a pile.
      const from =
        move.fromSlot >= 0
          ? this.last.get(spotKey(move.from, move.fromSlot))
          : hands(move.from);
      // Nothing was drawn at the source — the unit arrived and left inside one
      // tick, or the board was only just built. There is no journey to show.
      if (!from) continue;

      if (move.toSlot >= 0) {
        this.origin.set(spotKey(move.to, move.toSlot), { from, phase: move.phase });
      } else {
        this.transfers.push({ from, to: hands(move.to), phase: move.phase, into: true });
      }
    }

    // Only the passes that have something in them. A factory that dug but did
    // not cook plays two short phases, not four with two dead beats in them —
    // and a factory that did nothing at all plays none, which matters because
    // the render loop is on-demand and would otherwise spin every turn.
    const busy = new Set<Phase>(this.pulses.size > 0 ? ["cook"] : []);
    for (const arrival of this.origin.values()) busy.add(arrival.phase);
    for (const transfer of this.transfers) busy.add(transfer.phase);

    this.queue = ORDER.filter((phase) => busy.has(phase));
    this.at = 0;
    this.phase = this.queue.length > 0 ? 0 : 1;
  }

  /** Advance the run. True while any pass is still playing. */
  advance(dt: number): boolean {
    if (!this.running) return false;
    this.phase += dt / CARGO_PHASE_SECONDS;
    while (this.phase >= 1 && this.at < this.queue.length) {
      this.phase -= 1;
      this.at++;
    }
    if (this.at >= this.queue.length) {
      this.at = this.queue.length;
      this.phase = 1;
      return true; // one last frame, to seat everything at its resting place
    }
    return true;
  }

  /** True while any pass is still playing. */
  get running(): boolean {
    return this.at < this.queue.length;
  }

  /** The pass on screen right now, or null between runs. */
  get current(): Phase | null {
    return this.running ? this.queue[this.at] : null;
  }

  /**
   * How far through the current pass, eased.
   *
   * Smoothstep, not linear: a belt hands material on in one discrete step, and
   * a constant-velocity slide reads as the pile being dragged rather than as a
   * lane shunting it forward.
   */
  get t(): number {
    if (!this.running) return 1;
    const p = Math.min(1, Math.max(0, this.phase));
    return p * p * (3 - 2 * p);
  }

  /**
   * Has this pass happened yet?
   *
   * An ingot whose journey is still to come must not be drawn sitting at its
   * destination — the board holds the *end* state of the whole tick, so
   * everything is already where it finished. `-1` is "not yet", `0` is "now",
   * `1` is "done". A pass that is not in the queue never happens, so it counts
   * as done: nothing is waiting on it.
   */
  stageOf(phase: Phase): -1 | 0 | 1 {
    const index = this.queue.indexOf(phase);
    if (!this.running || index < 0) return 1;
    if (index === this.at) return 0;
    return index < this.at ? 1 : -1;
  }

  /** Where the ingot now at (square, index) set off from, and when. */
  arrivalFor(square: number, index: number): Arrival | undefined {
    const key = spotKey(square, index);
    const reported = this.origin.get(key);
    if (reported) return reported;
    const stayed = this.last.get(key);
    // It did not change places, so it has been in its own square the whole
    // time — the first pass is as good a moment as any to settle it.
    return stayed ? { from: stayed, phase: "flow" } : undefined;
  }

  /** Ingots between a lane and a machine's hands, on this pass only. */
  travelling(): readonly Transfer[] {
    const now = this.current;
    return now ? this.transfers.filter((t) => t.phase === now) : [];
  }

  /** Is this square's oven working, right now, on screen? */
  cooking(square: number): boolean {
    return this.current === "cook" && this.pulses.has(square);
  }

  /**
   * Record where everything came to rest, for the next tick to animate from.
   *
   * A fresh map every time, deliberately. Keeping entries around would leave a
   * stale spot under a key that no ingot occupies any more — and the next unit
   * to land there would slide in from wherever its predecessor happened to be
   * standing several turns ago.
   */
  settle(spots: Map<number, IngotSpot>): void {
    this.last = spots;
  }
}
